import primaryComponents from "../../primaryComponents";
import secondaryComponents from "..";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWarning, faPlus } from "@fortawesome/free-solid-svg-icons";
import styles from "./styles.module.scss";
import globalStyles from "../../../assets/styles/base/globalPage.module.scss";
import Validation from "./Validation";
import useJournalEntryForm from "./useJournalEntryForm";
import JournalLineTable from "./JournalLineTable";
import { formatNumberWithCommas } from "../../../helpers";

const JournalEntryForm = ({
  isEditMode = false,
  draftJournalEntryDetails = null,
}) => {
  const {
    storeList,
    journalEntryDetails,
    setJournalEntryDetails,
    useNarrationToLineDescription,
    setUseNarrationToLineDescription,
    addNewLine,
    taxOptions,
    taxOptionLabels,
    allChartOfAccounts,
    summary,
    handleJournalEntrySubmit,
    handleDraftJournalEntrySubmit,
    createJournalEntryMutation,
    draftJournalEntryMutation,
    suppliers,
    isLoadingSupplier,
  } = useJournalEntryForm(isEditMode, draftJournalEntryDetails);
  const { handleSubmit, ...validationParams } = Validation({
    journalEntryDetails,
  });

  return (
    <div className={styles.journalForm}>
      <div className={styles.warning}>
        <FontAwesomeIcon icon={faWarning} className="mr-2" />
        We recommend that only your accountant or bookkepper create journal
        unless you have experience managing your general ledger
      </div>
      <div className={styles.formSection}>
        <div className={styles.cardSession}>
          <div className={styles.inputSection}>
            <div style={{ width: "50%" }}>
              <primaryComponents.InputFieldV2
                control={validationParams.control}
                classNames="white"
                name="narration"
                label="Narration"
                placeholder="Enter a brief description of the journal entry"
                isRequired={true}
                value={journalEntryDetails?.narration}
                onChange={({ target: { value } }) => {
                  setJournalEntryDetails((prev) => ({
                    ...prev,
                    narration: value,
                  }));
                }}
                errorMessage={validationParams.formState.errors?.narration}
                onKeyUp={() =>
                  validationParams.formState.errors?.narration !== undefined &&
                  validationParams.trigger("narration")
                }
              />
            </div>
            <div style={{ width: "25%" }}>
              <primaryComponents.DateInputV2
                control={validationParams.control}
                classNames="white"
                name="entry_date"
                label="Date"
                placeholder="Select the date of the journal entry"
                isRequired={true}
                value={journalEntryDetails?.entry_date}
                onChange={({ target: { value } }) => {
                  setJournalEntryDetails((prev) => ({
                    ...prev,
                    entry_date: value,
                  }));
                }}
                errorMessage={validationParams.formState.errors?.entry_date}
                onKeyUp={() =>
                  validationParams.formState.errors?.entry_date !== undefined &&
                  validationParams.trigger("entry_date")
                }
                register={validationParams.register}
              />
            </div>
            <div style={{ width: "25%" }}>
              <primaryComponents.SelectFieldV2
                control={validationParams.control}
                name="store_id"
                id="store_id"
                label="Store"
                isRequired={true}
                classNames="white"
                options={storeList}
                value={
                  storeList?.find(
                    (storeItem) =>
                      `${storeItem?.id}` === `${journalEntryDetails?.store_id}`,
                  )?.name || "Select store"
                }
                onChange={(value) => {
                  setJournalEntryDetails((prev) => ({
                    ...prev,
                    store_id: value,
                  }));
                }}
                idKey="id"
                nameKey="name"
                errorMessage={validationParams.formState.errors?.store_id}
                onKeyUp={() =>
                  validationParams.formState.errors?.store_id !== undefined &&
                  validationParams.trigger("store_id")
                }
              />
            </div>
          </div>
          <div className="d-flex justify-content-between">
            <div className="d-flex">
              <primaryComponents.Checkbox
                control={validationParams.control}
                id={"useNarrationToLineDescription"}
                name="useNarrationToLineDescription"
                label="Default narration to journal line description"
                isChecked={useNarrationToLineDescription}
                onChange={(value) => {
                  const updatedLines = journalEntryDetails?.lines?.map(
                    (line, index) => {
                      const newDescription = value
                        ? journalEntryDetails?.narration
                        : line?.description;
                      validationParams.setValue(
                        `lines.${index}.description`,
                        newDescription,
                      );
                      return {
                        ...line,
                        description: newDescription,
                      };
                    },
                  );
                  setJournalEntryDetails((prevState) => ({
                    ...prevState,
                    lines: updatedLines,
                  }));

                  setUseNarrationToLineDescription(value);
                }}
              />
              <label
                htmlFor="useNarrationToLineDescription"
                className="mt-1 cursor-pointer"
              >
                Default narration to journal line description?
              </label>
            </div>
            <div style={{ width: "320px" }}>
              <primaryComponents.CustomerSelectField
                control={validationParams.control}
                name="customer_id"
                label="Customer"
                classNames="white"
                placeholder="Search customer by name or phone"
                value={journalEntryDetails?.customer_name || ""}
                onChange={(customerId, customer) => {
                  setJournalEntryDetails((prev) => ({
                    ...prev,
                    customer_id: customerId,
                    customer_name: customer?.full_name || "",
                    supplier_id: null,
                    supplier_name: "",
                  }));
                }}
                errorMessage={validationParams.formState.errors?.customer_id}
                floatError={false}
              />
            </div>
            <div style={{ width: "320px" }}>
              {isLoadingSupplier ? (
                "Loading Suppliers..."
              ) : (
                <primaryComponents.SelectFieldV2
                  key={journalEntryDetails.supplier_id || "no-supplier"}
                  control={validationParams.control}
                  name={"supplier_id"}
                  register={validationParams.register}
                  label="Supplier"
                  value={journalEntryDetails.supplier_name || "Select supplier"}
                  classNames="white"
                  options={suppliers}
                  idKey="id"
                  nameKey="name"
                  errorMessage={validationParams.formState.errors?.supplier_id}
                  onChange={(value, label) => {
                    setJournalEntryDetails((prevState) => ({
                      ...prevState,
                      supplier_id: value,
                      supplier_name: label || "",
                      customer_id: null,
                      customer_name: "",
                    }));
                  }}
                />
              )}
            </div>
            <div style={{ width: "320px" }}>
              <primaryComponents.SelectField
                control={validationParams.control}
                name="tax_type"
                id="tax_type"
                classNames="white"
                options={taxOptionLabels}
                value={
                  taxOptionLabels.find(
                    (option) => option.value === journalEntryDetails?.tax_type,
                  )?.label
                }
                onChange={(value) => {
                  const lines = journalEntryDetails?.lines || [];
                  const updatedLines = lines.map((line) => ({
                    ...line,
                    tax_rate:
                      value === taxOptions.no_tax ? 0 : line.tax_rate || 0,
                  }));
                  setJournalEntryDetails((prev) => ({
                    ...prev,
                    tax_type: value,
                    lines: updatedLines,
                  }));
                }}
                idKey="value"
                nameKey="label"
                label="Amounts are"
              />
            </div>
          </div>
        </div>
        <div className={styles.lineCardSession}>
          <secondaryComponents.LoaderHelper
            isLoading={allChartOfAccounts.isLoading}
            classNames="mt-5"
          >
            <JournalLineTable
              journalEntryDetails={journalEntryDetails}
              setJournalEntryDetails={setJournalEntryDetails}
              {...validationParams}
              taxOptions={taxOptions}
              allChartOfAccounts={allChartOfAccounts.data}
            />
          </secondaryComponents.LoaderHelper>

          <div className="d-flex justify-content-between mt-3 p-4">
            <primaryComponents.Button
              classNames="btn btn--outline"
              type="button"
              onClick={() => {
                addNewLine();
              }}
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Add a New Line
            </primaryComponents.Button>
            <div className={`${styles.summary}`}>
              <table style={{ width: "100%" }}>
                <tbody>
                  <tr>
                    <td>Subtotal</td>
                    <td>
                      {formatNumberWithCommas(
                        summary.subtotal_debit.toFixed(2),
                      )}
                    </td>
                    <td>
                      {formatNumberWithCommas(
                        summary.subtotal_credit.toFixed(2),
                      )}
                    </td>
                  </tr>
                  <tr
                    className={
                      summary.total_debit_tax > 0 ||
                      summary.total_credit_tax > 0
                        ? ""
                        : `${globalStyles.hideElement}`
                    }
                  >
                    <td>VAT</td>
                    <td>
                      {formatNumberWithCommas(
                        summary.total_debit_tax.toFixed(2),
                      )}
                    </td>
                    <td>
                      {formatNumberWithCommas(
                        summary.total_credit_tax.toFixed(2),
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>Total</td>
                    <td>
                      {formatNumberWithCommas(summary.total_debit.toFixed(2))}
                    </td>
                    <td>
                      {formatNumberWithCommas(summary.total_credit.toFixed(2))}
                    </td>
                  </tr>
                  <tr
                    className={
                      summary.accountNotBalanced
                        ? ""
                        : `${globalStyles.hideElement}`
                    }
                  >
                    <td></td>
                    <td>Total is out by:</td>
                    <td>
                      {formatNumberWithCommas(
                        (summary.total_debit - summary.total_credit).toFixed(2),
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div
          className="d-flex justify-content-end mt-3"
          style={{ gap: "10px" }}
        >
          <primaryComponents.Button
            classNames="btn btn--outline"
            isDisabled={
              createJournalEntryMutation.isLoading ||
              draftJournalEntryMutation.isLoading
            }
            isLoading={draftJournalEntryMutation.isLoading}
            onClick={handleSubmit(handleDraftJournalEntrySubmit)}
            loadingText={"Saving..."}
          >
            Save as Draft
          </primaryComponents.Button>
          <primaryComponents.Button
            classNames="btn btn--primary"
            isDisabled={
              createJournalEntryMutation.isLoading ||
              draftJournalEntryMutation.isLoading ||
              summary.accountNotBalanced
            }
            isLoading={createJournalEntryMutation.isLoading}
            onClick={handleSubmit(handleJournalEntrySubmit)}
            loadingText={"Posting..."}
          >
            Post
          </primaryComponents.Button>
        </div>
      </div>
    </div>
  );
};

export default JournalEntryForm;
